import type Account from '@/data/account/model';
import type { AccountSubscription } from '@/data/account/model';
import type Plan from '@/data/plan/model';
import type User from '@/data/user/model';
import Monolith from '@/shared/api/monolith';
import { isMobile } from '@/shared/util/device';
import type { WiwDispatch } from '@/store';

import Cookies from 'js-cookie';
import type { LDContext, LDMultiKindContext } from 'launchdarkly-js-client-sdk';
import { init as ldReduxInit } from '@/ld-redux';
import { round } from 'lodash';
import moment from 'moment-timezone';

export const LD_COOKIE_NAME = 'ldCookie';

export type MetricImportArgs = {
  key: string;
  contextKeys?: object;
  creationDate: number;
  metricValue?: number;
};

export type MetricContextKeys = {
  'anonymous-user'?: string;
  user?: string;
  account?: string;
};

export class LaunchDarkly {
  static init(user: User, account: Account, plan: Plan, dispatch: WiwDispatch) {
    const userId = user.get('id');

    // Make sure we don't keep old caches around.
    if (userId !== null) {
      this.clearOldKeys(userId);
    }

    const LDMultiContext = this.buildMultiContext(user, account, plan);
    return ldReduxInit({
      dispatch,
      contexts: LDMultiContext,
    });
  }

  // clearOldKeys will remove any launch darkly cache items
  // from local storage that are present for any user in the
  // current environment other than the currently signed in
  // one. It will not remove any other keys.
  static clearOldKeys = (currentUserId: number) => {
    const prefix = `ld:${CONFIG.LAUNCH_DARKLY_TOKEN}:`;
    const currentKey = `${prefix}${currentUserId}`;

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) {
        continue;
      }

      // If the key has the LD prefix for the current environment
      // but does not match the key for the current user then we
      // need to remove the old local storage item.
      if (key.startsWith(prefix) && key !== currentKey) {
        window.localStorage.removeItem(key);
      }
    }
  };

  // Format a datetime string to a number suitable for LaunchDarkly.
  static formatDatetime(datetime: string | boolean | null): number | boolean {
    return datetime && typeof datetime === 'string' && moment(new Date(datetime)).isValid()
      ? moment.utc(datetime).valueOf()
      : false;
  }

  // "How many days ago?" as a number or boolean suitable for LaunchDarkly.
  static daysAgo(datetime: string | boolean | null): number | boolean {
    return datetime && typeof datetime === 'string' && moment(new Date(datetime)).isValid()
      ? round(moment().diff(moment(datetime), 'seconds') / 86400, 4)
      : false;
  }

  static buildMultiContext(user: User, account: Account, plan: Plan): LDMultiKindContext {
    const multiContext: LDMultiKindContext = { kind: 'multi' };

    const anonLDContext = Cookies.get('ld_anon_context');
    if (anonLDContext) {
      multiContext['anonymous-user'] = this.buildAnonymousUserContext(anonLDContext);
    }

    multiContext.user = this.buildUserContext(user, account, plan);
    multiContext.account = this.buildAccountContext(account);

    return multiContext;
  }

  static buildAnonymousUserContext(ldAnonContext: string): LDContext {
    return {
      kind: 'anonymous-user',
      key: ldAnonContext,
    };
  }

  static buildAccountContext(account: Account): LDContext {
    const accountId = account && account.id !== null && account.id !== undefined ? account.id.toString() : undefined;

    return {
      key: `account:${accountId}`,
      kind: 'account',
      accountCreatedAt: this.formatDatetime(account.created_at),
      planId: account.plan_id,
      expectedEmployees: account.ref_employees,
      signupOrigin: account.signup_origin,
    };
  }

  static buildUserContext(user: User, account: Account, plan: Plan): LDContext {
    const userId = user && user.id !== null && user.id !== undefined ? user.id.toString() : undefined;
    const accountId = account && account.id !== null && account.id !== undefined ? account.id.toString() : undefined;
    const masterAccountId = account.isChild() ? account.master_id.toString() : accountId;
    const activeAccountSubscriptions = account.subscriptions.filter(
      (subscription: AccountSubscription) => subscription.is_active,
    );
    const isMobileDevice = isMobile();

    return {
      kind: 'user',
      key: `user:${userId}`,
      secondary: `account:${masterAccountId}`,
      country: account.country.iso2,
      email: user.email,
      name: user.fullName,
      firstName: user.first_name,
      lastName: user.last_name,
      anonymous: false,
      accountBillingType: account.billing_type,
      accountConvertedAt: this.formatDatetime(account.converted_at),
      accountConvertedDaysAgo: this.daysAgo(account.converted_at),
      accountCreatedAt: this.formatDatetime(account.created_at),
      accountCreatedAtDaysAgo: this.daysAgo(account.created_at),
      accountHasAttendanceFreemium: account.attendance_freemium,
      accountHasMaster: account.isChild(),
      accountId,
      accountIsActive: account.isActive(),
      accountIsMaster: account.isParent(),
      accountName: account.company,
      accountPlanCustom: account.plan_custom,
      accountPlanExpires: this.formatDatetime(account.plan_expires),
      accountRefEmployees: account.ref_employees,
      accountTrialCreatedAt: this.formatDatetime(account.trial_created_at),
      accountType: account.typeName(),
      accountSubscriptionTypes: activeAccountSubscriptions.map(
        (subscription: AccountSubscription) => subscription.type,
      ),
      accountSubscriptionPlanIds: activeAccountSubscriptions.map(
        (subscription: AccountSubscription) => subscription.plan.id,
      ),
      accountUserCount: account.user_count,
      attendanceTrialExpiresAt: this.formatDatetime(account.wb_expires),
      ldCookie: Cookies.get(LD_COOKIE_NAME) || '',
      // send null marketingFunnel as a string so that is/is not one of etc matchers may be used
      marketingFunnel: `${account.getSettings('marketing.funnel', null)}`,
      masterAccountId,
      masterPlanId: account.isChild() ? account.master_plan_id : account.plan_id,
      // api response for accountType does not match between react and monolith
      // add computed planAccountType which is computed the same way in both.
      planAccountType: plan.accountType(account),
      planEmployeeMax: plan.employees,
      planId: account.plan_id,
      planIsDemo: plan.isTrial(),
      planIsFreemium: plan.isFreemium(),
      planIsPaid: plan.isPaid(),
      planName: plan.name,
      planTier: plan.sortTier(),
      planType: plan.type,
      userCanBilling: user.canBilling(),
      userId: user.id,
      userIsActivated: user.activated,
      userIsDeleted: user.is_deleted,
      userIsHidden: user.is_hidden,
      userPhoneNumber: user.phone_number,
      userRole: user.roleName,
      isMobile: isMobileDevice,
    };
  }

  /*
  Used to send custom metric events to LD via a monolith endpoint that sends the event to their API endpoint for
  importing metric events. Can't be done via SDK. Really only used for experiments and data analysis

  The contextKeys will be set to match the identified context (multi or user)
   */
  static importMetric(metric: MetricImportArgs, user: User, account: Account) {
    const { key, creationDate } = metric;

    if (key && creationDate && user && account) {
      const anonLDContext = Cookies.get('ld_anon_context');
      const contextKeys: MetricContextKeys = {};

      if (anonLDContext) {
        contextKeys['anonymous-user'] = anonLDContext;
      }
      contextKeys.user = `user:${user.id}`;
      contextKeys.account = `account:${account.id}`;

      metric.contextKeys = contextKeys;
      Monolith.url('/launchdarkly/metric').post(metric);
    }
  }
}

export default LaunchDarkly;
